import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatCurrency, formatDate } from '../../../lib/formatters.js';

interface InstrumentItem {
  id: string;
  name?: string;
  memoNumber?: string;
  schemeCode?: string;
  programCode?: string;
  schemeType?: string;
  issuedAmount?: string;
  remainingAmount?: string;
  isoCurrencyCode?: string;
  validFrom: string;
  validTo: string;
  description?: string;
}

interface InstrumentsResponse {
  type: 'credit_memos' | 'schemes' | 'incentive_programs';
  items: InstrumentItem[];
}

interface SourceInstrumentPickerProps {
  claimType: string;
  value: string;
  onChange: (id: string, item: InstrumentItem | null) => void;
}

export function SourceInstrumentPicker({ claimType, value, onChange }: SourceInstrumentPickerProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<InstrumentItem[]>([]);
  const [loading, setLoading] = useState(false);

  const needsInstrument = !['OUTLET_RETURN', 'DAMAGE_EXPIRY', 'PROMO_DISCOUNT_SUBSIDY', 'VOLUME_REBATE', 'TRADE_SPEND_DRAWDOWN', 'OTHER'].includes(claimType);

  useEffect(() => {
    if (!claimType || !needsInstrument) return;
    setLoading(true);
    api.get<InstrumentsResponse>(`/claims/eligible-instruments?type=${claimType}`)
      .then((r) => setItems(r.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [claimType, needsInstrument]);

  if (!needsInstrument) {
    return (
      <p className="text-sm text-slate-500 italic">{t('wizard.labels.noSourceRequired')}</p>
    );
  }

  return (
    <div>
      <label className="block text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2">
        {t('wizard.labels.sourceInstrument')}
      </label>
      {loading ? (
        <p className="text-sm text-slate-400">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No active instruments found for this claim type.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.map((item) => {
            const label = item.memoNumber ?? item.name ?? item.schemeCode ?? item.programCode ?? item.id;
            const sub = item.description ?? (item.schemeType ? `Type: ${item.schemeType}` : '');
            const amountLabel = item.remainingAmount
              ? formatCurrency(Number(item.remainingAmount), item.isoCurrencyCode ?? 'IDR')
              : item.issuedAmount
                ? formatCurrency(Number(item.issuedAmount), item.isoCurrencyCode ?? 'IDR')
                : null;
            const selected = value === item.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onChange(item.id, item)}
                className={[
                  'w-full text-left px-4 py-3 border rounded transition-colors',
                  selected
                    ? 'border-copper bg-copper-soft text-ink'
                    : 'border-hairline bg-bone hover:border-copper-warm',
                ].join(' ')}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-semibold text-sm">{label}</span>
                    {sub && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{sub}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatDate(item.validFrom)} — {formatDate(item.validTo)}
                    </p>
                  </div>
                  {amountLabel && (
                    <span className="font-mono text-sm text-copper-deep shrink-0">{amountLabel}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
